import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from "react-i18next";

const OrganizationSettingsLayout = () => {
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    website: "",
    phone: "",
    contactEmail: "",
    currency: "",
    industry: "",
    address: "",
  });

  const { organization, authToken, showAlert } = useStore.getState();
  const { t } = useTranslation();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.get(
          `/organizations/${organization}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.response?.status_response === "OK") {
          const data = response.data.response.data;
          setFormData({
            description: data.description || "",
            website: data.website || "",
            phone: data.phone || "",
            contactEmail: data.contactEmail || "",
            currency: data.currency || "",
            industry: data.industry || "",
            address: data.address || "",
          });
        }
      } catch (err) {
        console.error(err);
        showAlert(t("organizations.settingsLoadError"), "warning");
      } finally {
        setLoadingData(false);
      }
    };

    if (organization) loadSettings();
  }, [organization]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await api.patch(
        `/organizations/${organization}/settings`,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response?.status_response === "OK") {
        showAlert(t("organizations.settingsSaveSuccess"), "info");
      } else {
        showAlert(t("organizations.settingsSaveError"), "warning");
      }
    } catch (err) {
      console.error(err);
      showAlert(t("organizations.settingsSaveError"), "warning");
    } finally {
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 64px)",
          pt: "64px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: "64px", px: 4, pb: 4 }}>
      <Paper sx={{ p: 4, maxWidth: 700, mx: "auto", mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {t("organizations.settingsTitle")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t("organizations.settingsDescription")}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={t("organizations.settingsWebsite")}
              value={formData.website}
              onChange={(e) => handleChange("website", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={t("organizations.settingsPhone")}
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={t("organizations.settingsContactEmail")}
              value={formData.contactEmail}
              onChange={(e) => handleChange("contactEmail", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={t("organizations.settingsCurrency")}
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={t("organizations.settingsIndustry")}
              value={formData.industry}
              onChange={(e) => handleChange("industry", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("organizations.settingsAddress")}
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <LoadingButton
              variant="contained"
              loading={saving}
              onClick={handleSubmit}
              loadingIndicator={t("saving")}
            >
              {t("save")}
            </LoadingButton>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default OrganizationSettingsLayout;